import dayjs from "dayjs";

import type {
  WorkRequirementMemberStatusEnumType,
  WorkRequirementScreeningAnswersType,
  WorkRequirementUsStateRuleType,
} from "@libs/db-schemas";

// Pure work requirement rules, kept in the shared API lib so live screening
// totals on the client always agree with the status the server stores.
//
// Dates are passed and returned as ISO `YYYY-MM-DD` strings.

export type WorkRequirementRuleType = Pick<
  WorkRequirementUsStateRuleType,
  | "requiredMonthlyHours"
  | "monthlyIncomeThreshold"
  | "lookbackMonths"
  | "minAge"
  | "maxAge"
>;

export type WorkRequirementHoursType = Pick<
  WorkRequirementScreeningAnswersType,
  | "employmentHours"
  | "employmentMonthlyIncome"
  | "jobTrainingHours"
  | "schoolHours"
  | "communityServiceHours"
>;

export type WorkRequirementProofKindType =
  | "PROOF_OF_WORK"
  | "PROOF_OF_EXEMPTION";

export type WorkRequirementProgressType =
  | "ACTION_NEEDED"
  | "UP_TO_DATE"
  | "COMPLETED";

const roundToOneDecimal = (value: number) => Math.round(value * 10) / 10;

// Monthly pay counts as hours at the state's hourly rate (threshold / required
// hours), and employment counts whichever of reported hours or pay is higher.
export const calculateWorkRequirementHours = ({
  rule,
  hours,
}: {
  rule: WorkRequirementRuleType;
  hours: WorkRequirementHoursType;
}): number => {
  const hourlyRate = rule.monthlyIncomeThreshold / rule.requiredMonthlyHours;
  const payAsHours = (hours.employmentMonthlyIncome ?? 0) / hourlyRate;
  const employmentHours = Math.max(hours.employmentHours ?? 0, payAsHours);

  return roundToOneDecimal(
    employmentHours +
      (hours.jobTrainingHours ?? 0) +
      (hours.schoolHours ?? 0) +
      (hours.communityServiceHours ?? 0),
  );
};

export const determineWorkRequirementMemberStatus = ({
  rule,
  answers,
}: {
  rule: WorkRequirementRuleType;
  answers: Pick<
    WorkRequirementScreeningAnswersType,
    "exemptions" | "hasQualifyingIncome"
  > &
    WorkRequirementHoursType;
}): WorkRequirementMemberStatusEnumType => {
  if (answers.exemptions.length > 0) {
    return "EXEMPT";
  }

  if (answers.hasQualifyingIncome) {
    return "MEETS_REQUIREMENTS";
  }

  const totalHours = calculateWorkRequirementHours({ rule, hours: answers });

  return totalHours >= rule.requiredMonthlyHours
    ? "MEETS_REQUIREMENTS"
    : "DOES_NOT_MEET_REQUIREMENTS";
};

export const getWorkRequirementRequiredDocumentCount = ({
  rule,
  status,
}: {
  rule: WorkRequirementRuleType;
  status: WorkRequirementMemberStatusEnumType | null;
}): number => {
  switch (status) {
    case "EXEMPT":
      return 1;
    case "MEETS_REQUIREMENTS":
      return rule.lookbackMonths;
    default:
      return 0;
  }
};

const ISO_DATE_FORMAT = "YYYY-MM-DD";

// The `lookbackMonths` calendar months before the work requirement date,
// oldest first, each as the first day of the month.
// e.g. 2027-01-01 with 3 → ["2026-10-01", "2026-11-01", "2026-12-01"]
export const getWorkRequirementLookbackMonths = ({
  workRequirementDate,
  lookbackMonths,
}: {
  workRequirementDate: string;
  lookbackMonths: number;
}): string[] => {
  const dueMonth = dayjs(workRequirementDate).startOf("month");

  return Array.from({ length: lookbackMonths }, (_, index) =>
    dueMonth.subtract(index + 1, "month").format(ISO_DATE_FORMAT),
  ).reverse();
};

// Proof of work can't be uploaded early: a month opens on its first day.
export const isWorkRequirementMonthUnlocked = ({
  coverageMonth,
  today,
}: {
  coverageMonth: string;
  today: string;
}): boolean => !dayjs(today).isBefore(coverageMonth, "day");

export const hasWorkRequirementMonthEnded = ({
  coverageMonth,
  today,
}: {
  coverageMonth: string;
  today: string;
}): boolean =>
  !dayjs(today).isBefore(
    dayjs(coverageMonth).startOf("month").add(1, "month"),
    "day",
  );

export const getAgeInYears = ({
  dateOfBirth,
  today,
}: {
  dateOfBirth: string;
  today: string;
}): number => dayjs(today).diff(dateOfBirth, "year");

export const isWithinWorkRequirementAgeRange = ({
  rule,
  dateOfBirth,
  today,
}: {
  rule: WorkRequirementRuleType;
  dateOfBirth: string | null | undefined;
  today: string;
}): boolean => {
  if (!dateOfBirth) {
    return false;
  }

  const age = getAgeInYears({ dateOfBirth, today });
  return age >= rule.minAge && age <= rule.maxAge;
};

export type WorkRequirementSlotInputType<TDocument> = {
  kind: WorkRequirementProofKindType;
  coverageMonth: string | null;
  isLocked: boolean;
  documents: TDocument[];
};

export const buildWorkRequirementMemberSlots = <
  TDocument extends { coverageMonth: string | null },
>({
  rule,
  status,
  workRequirementDate,
  documents,
  today,
}: {
  rule: WorkRequirementRuleType;
  status: WorkRequirementMemberStatusEnumType | null;
  workRequirementDate: string;
  documents: TDocument[];
  today: string;
}): WorkRequirementSlotInputType<TDocument>[] => {
  if (status === "EXEMPT") {
    return [
      {
        kind: "PROOF_OF_EXEMPTION",
        coverageMonth: null,
        isLocked: false,
        documents: documents.filter(document => !document.coverageMonth),
      },
    ];
  }

  if (status === "MEETS_REQUIREMENTS") {
    return getWorkRequirementLookbackMonths({
      workRequirementDate,
      lookbackMonths: rule.lookbackMonths,
    }).map(coverageMonth => ({
      kind: "PROOF_OF_WORK",
      coverageMonth,
      isLocked: !isWorkRequirementMonthUnlocked({ coverageMonth, today }),
      documents: documents.filter(
        document => document.coverageMonth === coverageMonth,
      ),
    }));
  }

  return [];
};

type WorkRequirementProgressMemberType = {
  slots: Pick<
    WorkRequirementSlotInputType<unknown>,
    "isLocked" | "documents"
  >[];
};

// Progress only reflects the proof still owed. The volunteer offramp click is
// tracked for reporting but never changes what a household needs to do.
export const getWorkRequirementProgress = ({
  members,
}: {
  members: WorkRequirementProgressMemberType[];
}): WorkRequirementProgressType => {
  const slots = members.flatMap(member => member.slots);

  if (slots.some(slot => !slot.isLocked && slot.documents.length === 0)) {
    return "ACTION_NEEDED";
  }

  const isEverySlotComplete = slots.every(slot => slot.documents.length > 0);
  return isEverySlotComplete ? "COMPLETED" : "UP_TO_DATE";
};
